import { useState, useEffect, useRef, type CSSProperties } from 'react';
import type { SDUIComponentProps } from '../../renderer/ComponentRenderer';
import { useArcheAction } from '../../hooks/useArcheAction';
import { useArcheContext } from '../../hooks/useArcheContext';
import { cn } from '@/lib/utils';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChatInterface } from './ChatInterface';
import { markdownClasses } from './markdown-classes';

/**
 * AIOutput component — display-first AI output with stop/continue controls.
 *
 * Shows clean Markdown during streaming with a Stop button.
 * On Stop or Continue, transitions to a full ChatInterface so the user
 * can type adjustments.
 *
 * Use for primarily-display AI output (generate, summarize, translate).
 * Use ChatInterface instead for conversation-first interfaces.
 */
export function AIOutput({ id, props, style }: SDUIComponentProps) {
  const { isAIStreaming, aiStreamingText, aiThinkingText, stopAIStreaming, updateInput } =
    useArcheAction();
  const { context } = useArcheContext();

  const {
    configId,
    messagesKey = 'chatMessages',
    outputKey = 'chatResponse',
    placeholderText = 'AI-generated content will appear here.',
    showThinking = true,
  } = props as {
    configId: string;
    sendActionId: string;
    messagesKey?: string;
    outputKey?: string;
    placeholder?: string;
    placeholderText?: string;
    showThinking?: boolean;
  };

  const [chatMode, setChatMode] = useState(false);
  const [hasReceivedOutput, setHasReceivedOutput] = useState(false);
  const prevIsStreamingRef = useRef(false);

  const streamingText = configId ? aiStreamingText[configId] : undefined;
  const thinkingText = configId ? aiThinkingText[configId] : undefined;
  const outputText = context.output?.[outputKey] as string | undefined;

  // Track when streaming completes (true→false) so we know output is done
  useEffect(() => {
    if (prevIsStreamingRef.current && !isAIStreaming && outputText) {
      setHasReceivedOutput(true);
    }
    prevIsStreamingRef.current = isAIStreaming;
  }, [isAIStreaming, outputText]);

  const handleStop = () => {
    stopAIStreaming();
    // Read partial text from streaming or output
    const partialText = streamingText || outputText || '';
    if (partialText) {
      updateInput(messagesKey, [{ role: 'assistant', content: partialText }]);
    }
    setChatMode(true);
  };

  const handleModify = () => {
    const fullText = outputText || '';
    if (fullText) {
      updateInput(messagesKey, [{ role: 'assistant', content: fullText }]);
    }
    setChatMode(true);
  };

  // If in chat mode, delegate everything to ChatInterface
  if (chatMode) {
    return <ChatInterface id={id} props={props} style={style} />;
  }

  const isStreaming = isAIStreaming;
  const hasOutput = hasReceivedOutput && !!outputText;
  const hasStreamingText = isStreaming && !!streamingText;
  const isIdle = !isStreaming && !hasOutput;

  return (
    <div id={id} className="flex flex-col" style={style as CSSProperties} data-testid="ai-output">
      {/* Output area */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Idle placeholder — visible before generation starts */}
        {isIdle && (
          <div
            className="flex items-center justify-center min-h-[120px] rounded-lg border border-dashed border-border bg-muted/30 text-muted-foreground text-sm italic"
            data-testid="idle-placeholder"
          >
            {placeholderText}
          </div>
        )}

        {/* Thinking indicator */}
        {isStreaming && showThinking && thinkingText && !streamingText && (
          <div
            className="text-muted-foreground text-sm italic mb-2"
            data-testid="thinking-indicator"
          >
            Thinking...
          </div>
        )}

        {/* Streaming text */}
        {hasStreamingText && (
          <div className={markdownClasses} data-testid="streaming-output">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{streamingText!}</ReactMarkdown>
            <span
              className="inline-block w-2 h-2 bg-foreground/50 rounded-full animate-pulse ml-1"
              data-testid="streaming-indicator"
            />
          </div>
        )}

        {/* Loading dots: streaming with no text yet */}
        {isStreaming && !streamingText && !thinkingText && (
          <div className="flex gap-1" data-testid="loading-dots">
            <span className="w-2 h-2 bg-foreground/40 rounded-full animate-bounce [animation-delay:0ms]" />
            <span className="w-2 h-2 bg-foreground/40 rounded-full animate-bounce [animation-delay:150ms]" />
            <span className="w-2 h-2 bg-foreground/40 rounded-full animate-bounce [animation-delay:300ms]" />
          </div>
        )}

        {/* Completed output */}
        {hasOutput && !isStreaming && (
          <div className={markdownClasses} data-testid="completed-output">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{outputText!}</ReactMarkdown>
          </div>
        )}
      </div>

      {/* Controls — only shown when there's something to act on */}
      {!isIdle && (
        <div className="flex-shrink-0 border-t border-border p-3 flex justify-end gap-2">
          {isStreaming && (
            <button
              type="button"
              onClick={handleStop}
              className={cn(
                'rounded-lg px-3 py-2 text-sm font-medium',
                'bg-destructive text-destructive-foreground hover:bg-destructive/90',
              )}
              data-testid="stop-button"
            >
              Stop
            </button>
          )}

          {hasOutput && !isStreaming && (
            <button
              type="button"
              onClick={handleModify}
              className={cn(
                'rounded-lg px-3 py-2 text-sm font-medium',
                'bg-secondary text-secondary-foreground hover:bg-secondary/80',
              )}
              data-testid="modify-button"
            >
              Modify
            </button>
          )}
        </div>
      )}
    </div>
  );
}
